/**
 * GuardrailPicker — a compact multi-select for choosing guardrails to include
 * when generating a prompt with AI.
 *
 * Fetches the tenant's enabled guardrails from `/admin/ai/guardrails` and
 * calls `onChange` with both the selected IDs and the resolved content
 * strings, so the caller can pass `guardrailContents` straight to the
 * generate-prompt API endpoints (`/admin/ai/prompts/generate` and
 * `/admin/ai/agents/generate-prompt`).
 *
 * Reused by both `PromptFormDrawer` and `EditAgentDrawer` so the option is
 * available wherever a prompt is generated.
 */
import { useCallback, useEffect, useState, type JSX } from 'react'
import { Shield, Check, ChevronDown, ChevronUp } from 'lucide-react'
import { api } from '../../../api/client.js'
import { useToast } from '../../system/ToastProvider.js'

interface GuardrailOption {
  id: string
  slug: string
  name: string
  content: string
  category: string | null
  isEnabled: boolean
}

interface GuardrailPickerProps {
  /** IDs of the currently selected guardrails. */
  selectedIds: Set<string>
  /**
   * Called with the updated set of selected guardrail IDs AND the resolved
   * content strings of those guardrails, so the caller can pass
   * `guardrailContents` directly to the generate-prompt API.
   */
  onChange: (ids: Set<string>, contents: string[]) => void
  /** Optional label override. */
  label?: string
}

export function GuardrailPicker({ selectedIds, onChange, label }: GuardrailPickerProps): JSX.Element {
  const toast = useToast()
  const [guardrails, setGuardrails] = useState<GuardrailOption[]>([])
  const [expanded, setExpanded] = useState(false)

  const loadGuardrails = useCallback(async () => {
    try {
      const data = await api.get<{ guardrails: GuardrailOption[] }>('/admin/ai/guardrails')
      // Only show enabled guardrails — disabled ones aren't applied at runtime
      // so offering them in the picker would be misleading.
      setGuardrails((data.guardrails ?? []).filter((g) => g.isEnabled))
    } catch (err) {
      toast.error('Failed to load guardrails', err instanceof Error ? err.message : String(err))
    }
  }, [toast])

  useEffect(() => {
    void loadGuardrails()
  }, [loadGuardrails])

  const resolveContents = (ids: Set<string>): string[] =>
    guardrails
      .filter((g) => ids.has(g.id))
      .map((g) => g.content)
      .filter((c) => c.trim().length > 0)

  const toggle = (id: string): void => {
    const next = new Set(selectedIds)
    if (next.has(id)) {
      next.delete(id)
    } else {
      next.add(id)
    }
    onChange(next, resolveContents(next))
  }

  if (guardrails.length === 0) {
    return (
      <div>
        <label className="mb-1 block text-[10px] font-medium uppercase tracking-wide text-gray-500">
          {label ?? 'Guardrails to include'}
        </label>
        <p className="text-[10px] text-gray-400">
          No enabled guardrails configured. Create guardrails in the Guardrails tab to include them here.
        </p>
      </div>
    )
  }

  const selectedCount = selectedIds.size

  return (
    <div>
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        className="flex w-full items-center justify-between rounded-sm border border-gray-200 px-2.5 py-1.5 text-[11px] font-medium text-gray-700 transition-colors hover:bg-gray-50"
      >
        <span className="flex items-center gap-1.5">
          <Shield className="h-3 w-3 text-blue-500" />
          {label ?? 'Guardrails to include'}
          {selectedCount > 0 && (
            <span className="rounded-full bg-blue-100 px-1.5 py-0.5 text-[9px] font-semibold text-blue-700">
              {selectedCount} selected
            </span>
          )}
        </span>
        {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
      </button>

      {expanded && (
        <div className="mt-1 max-h-40 space-y-1 overflow-y-auto rounded-sm border border-gray-200 bg-white p-1.5">
          {guardrails.map((g) => {
            const isSelected = selectedIds.has(g.id)
            return (
              <button
                key={g.id}
                type="button"
                onClick={() => toggle(g.id)}
                className={`flex w-full items-start gap-2 rounded-sm px-2 py-1.5 text-left text-[10px] transition-colors ${
                  isSelected ? 'bg-blue-50 text-blue-900' : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <span
                  className={`mt-0.5 flex h-3 w-3 flex-shrink-0 items-center justify-center rounded-sm border ${
                    isSelected ? 'border-blue-500 bg-blue-500' : 'border-gray-300'
                  }`}
                >
                  {isSelected && <Check className="h-2 w-2 text-white" />}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-medium">{g.name}</span>
                  {g.category && (
                    <span className="text-[9px] text-gray-400">{g.category}</span>
                  )}
                </span>
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
